import { useState, useEffect } from "react";
import {
  TextField,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
  Grid,
  Typography,
  Button,
  Divider,
  Stack,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Alert,
  Autocomplete,
  CircularProgress
} from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.js';
import StandardFormDialog from '../../components/StandardFormDialog.jsx';
import { formatarCPF, formatarTelefone, formatarData, converterDataParaBR, converterDataParaISO, formatarCEP } from '../../utils/formatadores.js';
import { ESTADOS_BRASILEIROS } from '../../utils/constants.js';
import { sharedStyles } from '../../styles/sharedStyles.js';



// --- Design System Components ---
const FormSection = ({ title, children, variant = 'default', action }) => {
  const isHighlight = variant === 'highlight';
  const isError = variant === 'error';

  return (
    <Box sx={{
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      p: (isHighlight || isError) ? '16px' : 0,
      bgcolor: isHighlight ? 'grey.50' : (isError ? 'error.50' : 'transparent'),
      borderRadius: 2,
      border: (isHighlight || isError) ? '1px solid' : 'none',
      borderColor: 'divider',
      mb: '24px'
    }}>
      {(title || action) && (
        <Box sx={{ mb: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                color: isError ? 'error.main' : 'text.primary',
                textTransform: 'uppercase',
                letterSpacing: 1,
                fontSize: '0.875rem'
              }}
            >
              {title}
            </Typography>
          )}
          {action && <Box>{action}</Box>}
        </Box>
      )}
      {children}
    </Box>
  );
};

// Modificado para garantir espaçamento e largura corretos - sempre ocupa 100% da largura
const FormRow = ({ children, sx, ...props }) => (
  <Grid container spacing={2} sx={{ width: '100%', maxWidth: '100%', m: 0, mb: 2, boxSizing: 'border-box', ...sx }} {...props}>
    {children}
  </Grid>
);

const TabContainer = ({ children }) => (
  <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', minHeight: '520px', display: 'flex', flexDirection: 'column', gap: '16px', p: 0 }}>
    {children}
  </Box>
);

export default function CadastroUsuario() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [openForm, setOpenForm] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresasOrdenadas, setEmpresasOrdenadas] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const [formData, setFormData] = useState({
    st_nome: "",
    st_email: "",
    st_senha: "",
    st_confirmar_senha: "",
    st_telefone: "",
    en_perfil: "beneficiario",
    id_empresa: "",
    bo_acesso_temporario: false,
    dt_expiracao_acesso: "",
    st_dados_complementares: {
      rg: "",
      cpf: "",
      endereco: {
        cep: "",
        logradouro: "",
        numero: "",
        bairro: "",
        cidade: "",
        estado: "",
        pais: "Brasil"
      },
      dados_pessoais: {
        dt_nascimento: "",
        en_sexo: "",
        en_estado_civil: "",
        st_nacionalidade: "Brasileira",
        st_naturalidade: "",
        st_nome_mae: "",
        st_nome_pai: ""
      },
      documentos: {
        cpf: "",
        rg: { numero: "", orgao: "", uf: "" },
        pis_pasep: "",
        cnh: "",
        titulo_eleitor: { numero: "", zona: "", secao: "" },
        certificado_reservista: "",
        ctps: { numero: "", serie: "", uf: "" }
      },
      financeiro: {
        banco: "",
        agencia: "",
        conta: "",
        tipo_conta: "corrente",
        chave_pix: ""
      },
      contrato: {
        cargo: "",
        setor: "",
        dt_admissao: "",
        tipo_contrato: "clt",
        jornada: "",
        salario: "",
        beneficios: ""
      },
      rh: {
        formacao: "",
        cursos: "",
        experiencia: "",
        contato_emergencia: { nome: "", telefone: "", relacao: "" }
      },
      arquivos: {
        foto_perfil: null,
        docs_pessoais: null,
        comprovante_residencia: null,
        aso: null
      },
      dependentes: [],
      lgpd_consentimento: false,
      st_observacoes: ""
    }
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    carregarEmpresas();
    if (id) {
      carregarUsuario();
    } else if (user && user.id_empresa) {
      setFormData(prev => ({ ...prev, id_empresa: user.id_empresa }));
    }
  }, [id, user]);

  useEffect(() => {
    const isValid = () => {
      if (!formData.st_nome?.trim()) return false;
      if (!formData.st_email?.trim() || !/\S+@\S+\.\S+/.test(formData.st_email)) return false;
      if (!formData.id_empresa) return false;
      if (!formData.en_perfil) return false;

      // Validação de senha
      if (!id) {
        // Criação: senha obrigatória
        if (!formData.st_senha) return false;
        if (formData.st_senha !== formData.st_confirmar_senha) return false;
      } else {
        // Edição: se informou senha, tem que confirmar igual
        if (formData.st_senha && formData.st_senha !== formData.st_confirmar_senha) return false;
      }

      if (formData.bo_acesso_temporario && !formData.dt_expiracao_acesso) return false;

      return true;
    };
    setIsFormValid(isValid());
  }, [formData, id]);

  const carregarEmpresas = async () => {
    try {
      setLoadingEmpresas(true);
      const response = await api.get('/empresa');
      let empresasData = [];
      if (response.data && Array.isArray(response.data.dados)) {
        empresasData = response.data.dados;
      } else if (Array.isArray(response.data)) {
        empresasData = response.data;
      }
      setEmpresas(empresasData);

      // Ordenar empresas alfabeticamente
      if (Array.isArray(empresasData) && empresasData.length > 0) {
        const ordenadas = [...empresasData].sort((a, b) => {
          const nomeA = (a.st_nome || '').toLowerCase();
          const nomeB = (b.st_nome || '').toLowerCase();
          return nomeA.localeCompare(nomeB);
        });
        setEmpresasOrdenadas(ordenadas);
      } else {
        setEmpresasOrdenadas([]);
      }
    } catch (err) {
      console.error("Erro ao carregar empresas:", err);
      setError("Erro ao carregar lista de empresas");
      setEmpresasOrdenadas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  };

  const carregarUsuario = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/usuario/${id}`);
      const usuario = response.data.dados || response.data;

      setFormData(prev => ({
        ...prev,
        ...usuario,
        st_senha: "",
        st_confirmar_senha: "",
        bo_acesso_temporario: usuario.bo_acesso_temporario || false,
        dt_expiracao_acesso: converterDataParaBR(usuario.dt_expiracao_acesso || ""),
        st_dados_complementares: {
          ...prev.st_dados_complementares,
          ...usuario.st_dados_complementares,
          endereco: { ...prev.st_dados_complementares.endereco, ...(usuario.st_dados_complementares?.endereco || {}) },
          dados_pessoais: {
            ...prev.st_dados_complementares.dados_pessoais,
            ...(usuario.st_dados_complementares?.dados_pessoais || {}),
            dt_nascimento: converterDataParaBR(usuario.st_dados_complementares?.dados_pessoais?.dt_nascimento || "")
          },
          documentos: {
            ...prev.st_dados_complementares.documentos,
            ...(usuario.st_dados_complementares?.documentos || {}),
            rg: { ...prev.st_dados_complementares.documentos.rg, ...(usuario.st_dados_complementares?.documentos?.rg || {}) },
            titulo_eleitor: { ...prev.st_dados_complementares.documentos.titulo_eleitor, ...(usuario.st_dados_complementares?.documentos?.titulo_eleitor || {}) },
            ctps: { ...prev.st_dados_complementares.documentos.ctps, ...(usuario.st_dados_complementares?.documentos?.ctps || {}) }
          },
          financeiro: { ...prev.st_dados_complementares.financeiro, ...(usuario.st_dados_complementares?.financeiro || {}) },
          contrato: {
            ...prev.st_dados_complementares.contrato,
            ...(usuario.st_dados_complementares?.contrato || {}),
            dt_admissao: converterDataParaBR(usuario.st_dados_complementares?.contrato?.dt_admissao || "")
          },
          rh: {
            ...prev.st_dados_complementares.rh,
            ...(usuario.st_dados_complementares?.rh || {}),
            contato_emergencia: { ...prev.st_dados_complementares.rh.contato_emergencia, ...(usuario.st_dados_complementares?.rh?.contato_emergencia || {}) }
          },
          arquivos: { ...prev.st_dados_complementares.arquivos },
          dependentes: (usuario.st_dados_complementares?.dependentes || []).map(dep => ({
            ...dep,
            dt_nascimento: converterDataParaBR(dep.dt_nascimento || "")
          }))
        }
      }));
    } catch (err) {
      console.error("Erro ao carregar usuário:", err);
      setError("Erro ao carregar dados do usuário");
    } finally {
      setLoading(false);
    }
  };

  const buscarCEP = async (cep) => {
    const limpo = cep.replace(/\D/g, '');
    if (limpo.length !== 8) return;

    try {
      setLoading(true);
      const response = await axios.get(`https://viacep.com.br/ws/${limpo}/json/`);
      if (response.data.erro) {
        setError("CEP não encontrado.");
        return;
      }

      const { logradouro, bairro, localidade, uf } = response.data;
      setFormData(prev => ({
        ...prev,
        st_dados_complementares: {
          ...prev.st_dados_complementares,
          endereco: {
            ...prev.st_dados_complementares.endereco,
            logradouro,
            bairro,
            cidade: localidade,
            estado: uf,
            cep: formatarCEP(limpo)
          }
        }
      }));
    } catch (err) {
      console.error("Erro ao buscar CEP:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'st_telefone') {
      value = formatarTelefone(value);
    }
    if (name === 'dt_expiracao_acesso') {
      value = formatarData(value);
    }
    setFormData(prev => ({ ...prev, [name]: value }));

    // Limpar erro do campo ao digitar
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleComplementarChange = (section, field, value, subfield = null) => {
    let formattedValue = value;

    if (section === 'endereco' && field === 'cep') {
      formattedValue = formatarCEP(value);
      if (formattedValue.length === 9) {
        buscarCEP(formattedValue);
      }
    }
    if (section === 'documentos' && field === 'cpf') {
      formattedValue = formatarCPF(value);
    }
    if (section === 'rh' && field === 'contato_emergencia' && subfield === 'telefone') {
      formattedValue = formatarTelefone(value);
    }
    if ((section === 'dados_pessoais' && field === 'dt_nascimento') ||
      (section === 'contrato' && field === 'dt_admissao')) {
      formattedValue = formatarData(value);
    }

    setFormData(prev => {
      const newData = { ...prev };
      if (subfield) {
        newData.st_dados_complementares[section][field][subfield] = formattedValue;
      } else {
        newData.st_dados_complementares[section][field] = formattedValue;
      }
      return newData;
    });
  };

  const addDependente = () => {
    setFormData(prev => ({
      ...prev,
      st_dados_complementares: {
        ...prev.st_dados_complementares,
        dependentes: [...(prev.st_dados_complementares.dependentes || []), { nome: '', dt_nascimento: '', parentesco: '', cpf: '' }]
      }
    }));
  };

  const removeDependente = (index) => {
    setFormData(prev => ({
      ...prev,
      st_dados_complementares: {
        ...prev.st_dados_complementares,
        dependentes: prev.st_dados_complementares.dependentes.filter((_, i) => i !== index)
      }
    }));
  };

  const handleDependenteChange = (index, field, value) => {
    if (field === 'cpf') value = formatarCPF(value);
    if (field === 'dt_nascimento') value = formatarData(value);

    setFormData(prev => {
      const newDependentes = [...(prev.st_dados_complementares.dependentes || [])];
      newDependentes[index] = { ...newDependentes[index], [field]: value };
      return {
        ...prev,
        st_dados_complementares: {
          ...prev.st_dados_complementares,
          dependentes: newDependentes
        }
      };
    });
  };

  const handleFileChange = (e, docKey) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFormData(prev => ({
        ...prev,
        st_dados_complementares: {
          ...prev.st_dados_complementares,
          arquivos: {
            ...prev.st_dados_complementares.arquivos,
            [docKey]: file
          }
        }
      }));
    }
  };

  const removeFile = (docKey) => {
    setFormData(prev => ({
      ...prev,
      st_dados_complementares: {
        ...prev.st_dados_complementares,
        arquivos: {
          ...prev.st_dados_complementares.arquivos,
          [docKey]: null
        }
      }
    }));
  };

  const validarFormulario = () => {
    const errors = {};

    if (!formData.st_nome) errors.st_nome = "Nome completo é obrigatório";

    if (!formData.st_email) {
      errors.st_email = "E-mail é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(formData.st_email)) {
      errors.st_email = "E-mail inválido";
    }

    if (!formData.id_empresa) errors.id_empresa = "Empresa é obrigatória";
    if (!formData.en_perfil) errors.en_perfil = "Perfil é obrigatório";

    if (!id) {
      if (!formData.st_senha) errors.st_senha = "Senha é obrigatória";
    }

    if (formData.st_senha && formData.st_senha !== formData.st_confirmar_senha) {
      errors.st_confirmar_senha = "As senhas não conferem";
    }

    if (formData.bo_acesso_temporario && !formData.dt_expiracao_acesso) {
      errors.dt_expiracao_acesso = "Data de expiração é obrigatória";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validarFormulario()) {
      setError("Preencha todos os campos obrigatórios (*).");
      return;
    }

    try {
      setLoading(true);
      const payload = { ...formData }; // Clone para não alterar o state visual

      // Converter datas para ISO antes de enviar
      payload.dt_expiracao_acesso = converterDataParaISO(payload.dt_expiracao_acesso);

      // Clone e conversão dos dados complementares
      payload.st_dados_complementares = { ...payload.st_dados_complementares };

      if (payload.st_dados_complementares.dados_pessoais) {
        payload.st_dados_complementares.dados_pessoais = {
          ...payload.st_dados_complementares.dados_pessoais,
          dt_nascimento: converterDataParaISO(payload.st_dados_complementares.dados_pessoais.dt_nascimento)
        };
      }

      if (payload.st_dados_complementares.contrato) {
        payload.st_dados_complementares.contrato = {
          ...payload.st_dados_complementares.contrato,
          dt_admissao: converterDataParaISO(payload.st_dados_complementares.contrato.dt_admissao)
        };
      }

      if (payload.st_dados_complementares.dependentes) {
        payload.st_dados_complementares.dependentes = payload.st_dados_complementares.dependentes.map(dep => ({
          ...dep,
          dt_nascimento: converterDataParaISO(dep.dt_nascimento)
        }));
      }

      if (id) {
        await api.put(`/usuario/${id}`, payload);
        setSuccess("Usuário atualizado com sucesso!");
      } else {
        await api.post('/usuario', payload);
        setSuccess("Usuário cadastrado com sucesso!");
      }
      setTimeout(() => navigate('/usuarios'), 1500);
    } catch (err) {
      console.error(err);
      const errorMsg = err.response?.data?.mensagem || err.response?.data?.error || "Erro ao salvar usuário";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLGPD = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Por favor, habilite popups para imprimir o termo.");
      return;
    }

    const empresaNome = empresas.find(e => e.id_empresa === formData.id_empresa)?.st_nome || "Empresa";
    const nomeUsuario = formData.st_nome || "__________________________________________";
    const cpfUsuario = formData.st_dados_complementares.documentos.cpf || "__________________";
    const dataAtual = new Date().toLocaleDateString('pt-BR');

    const htmlContent = `
      <html>
        <head>
          <title>Termo de Consentimento LGPD</title>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 40px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .content { text-align: justify; margin-bottom: 40px; }
          </style>
        </head>
        <body>
          <div class="header"><h2>${empresaNome}</h2></div>
          <p>TERMO DE CONSENTIMENTO LGPD</p>
          <p>Eu, ${nomeUsuario}, CPF ${cpfUsuario}, declaro que aceito...</p>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const verificarPerfil = (perfisPermitidos) => {
    return user && perfisPermitidos.includes(user.perfil);
  };

  return (
    <StandardFormDialog
      title={id ? "Editar Usuário" : "Novo Usuário"}
      open={openForm}
      onClose={() => navigate('/usuarios')}
      onSave={handleSubmit}
      disableSave={!isFormValid}
      loading={loading}
      error={error}
      onErrorClose={() => setError(null)}
      success={success}
      onSuccessClose={() => setSuccess(null)}
      maxWidth="lg"
      tabs={[
        {
          label: "Dados Básicos",
          content: (
            <TabContainer>
              <FormSection title="Dados Principais">
                {/* Linha 1 */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Nome Completo *" name="st_nome" value={formData.st_nome} onChange={handleChange} required disabled={loading} error={!!fieldErrors.st_nome} helperText={fieldErrors.st_nome} />
                    <TextField fullWidth label="E-mail *" name="st_email" type="email" value={formData.st_email} onChange={handleChange} required disabled={loading} error={!!fieldErrors.st_email} helperText={fieldErrors.st_email} />
                  </Box>

                  {/* Linha 2 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Telefone" name="st_telefone" value={formData.st_telefone} onChange={handleChange} disabled={loading} />
                    <Autocomplete
                      fullWidth
                      options={empresasOrdenadas}
                      getOptionLabel={(option) => option.st_nome || ''}
                      value={empresasOrdenadas.find(emp => emp.id_empresa === formData.id_empresa) || null}
                      onChange={(event, newValue) => {
                        setFormData(prev => ({
                          ...prev,
                          id_empresa: newValue ? newValue.id_empresa : ''
                        }));
                        if (fieldErrors.id_empresa) {
                          setFieldErrors(prev => ({ ...prev, id_empresa: null }));
                        }
                      }}
                      disabled={loading || loadingEmpresas || (user?.en_perfil !== 'superusuario' && user?.id_empresa)}
                      loading={loadingEmpresas}
                      filterOptions={(options, { inputValue }) => {
                        const filterValue = inputValue.toLowerCase();
                        return options.filter(option => {
                          const nome = (option.st_nome || '').toLowerCase();
                          const cpfCnpj = (option.st_cpfCnpj || '').toLowerCase();
                          return nome.includes(filterValue) || cpfCnpj.includes(filterValue);
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Empresa *"
                          required
                          error={!!fieldErrors.id_empresa}
                          helperText={fieldErrors.id_empresa}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingEmpresas ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <Box>
                            <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                              {option.st_nome}
                            </Typography>
                            {option.st_cpfCnpj && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {option.st_cpfCnpj}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      noOptionsText={loadingEmpresas ? "Carregando empresas..." : "Nenhuma empresa encontrada"}
                      sx={{
                        width: '100%',
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem'
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.875rem'
                        }
                      }}
                    />
                    <FormControl fullWidth sx={sharedStyles.formField} error={!!fieldErrors.en_perfil}>
                      <InputLabel>Perfil *</InputLabel>
                      <Select label="Perfil *" name="en_perfil" value={formData.en_perfil} onChange={handleChange} required>
                        <MenuItem value="beneficiario">Beneficiário</MenuItem>
                        <MenuItem value="operador">Operador</MenuItem>
                        {verificarPerfil(['superusuario', 'gerente']) && <MenuItem value="administrador">Administrador</MenuItem>}
                        {verificarPerfil(['superusuario', 'gerente']) && <MenuItem value="gerente">Gerente</MenuItem>}
                        {verificarPerfil(['superusuario']) && <MenuItem value="superusuario">Superusuário</MenuItem>}
                      </Select>
                      {fieldErrors.en_perfil && <FormHelperText>{fieldErrors.en_perfil}</FormHelperText>}
                    </FormControl>
                  </Box>
                </Box>
              </FormSection>

              <FormSection title="Segurança da Conta" variant="error">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Senhas */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label={id ? "Nova Senha" : "Senha *"} name="st_senha" type={showPassword ? 'text' : 'password'} value={formData.st_senha} onChange={handleChange} required={!id} error={!!fieldErrors.st_senha} helperText={fieldErrors.st_senha} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                    <TextField fullWidth label={id ? "Confirmar Nova Senha" : "Confirmar Senha *"} name="st_confirmar_senha" type={showConfirmPassword ? 'text' : 'password'} value={formData.st_confirmar_senha} onChange={handleChange} required={!id} error={!!fieldErrors.st_confirmar_senha} helperText={fieldErrors.st_confirmar_senha} InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>{showConfirmPassword ? <VisibilityOff /> : <Visibility />}</IconButton></InputAdornment> }} />
                  </Box>

                  {/* Validade de Acesso */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.bo_acesso_temporario}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              bo_acesso_temporario: e.target.checked,
                              dt_expiracao_acesso: e.target.checked ? prev.dt_expiracao_acesso : ""
                            }));
                            if (!e.target.checked && fieldErrors.dt_expiracao_acesso) {
                              setFieldErrors(prev => ({ ...prev, dt_expiracao_acesso: null }));
                            }
                          }}
                        />
                      }
                      label="Acesso Temporário"
                      sx={{ flex: '0 0 auto' }}
                    />
                    {formData.bo_acesso_temporario && (
                      <TextField
                        fullWidth
                        label="Data de Expiração do Acesso *"
                        name="dt_expiracao_acesso"
                        value={formData.dt_expiracao_acesso}
                        onChange={handleChange}
                        required={formData.bo_acesso_temporario}
                        error={!!fieldErrors.dt_expiracao_acesso}
                        helperText={fieldErrors.dt_expiracao_acesso || "Data em que o acesso do usuário expirará"}
                        placeholder="DD/MM/AAAA"
                        inputProps={{ maxLength: 10 }}
                      />
                    )}
                  </Box>
                </Box>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "Dados Pessoais",
          content: (
            <TabContainer>
              <FormSection>
                {/* Linha 1 - Info Pessoal Básica */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Data de Nascimento" value={formData.st_dados_complementares.dados_pessoais.dt_nascimento} onChange={(e) => handleComplementarChange('dados_pessoais', 'dt_nascimento', e.target.value)} placeholder="DD/MM/AAAA" inputProps={{ maxLength: 10 }} />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Sexo / Gênero</InputLabel>
                      <Select label="Sexo / Gênero" value={formData.st_dados_complementares.dados_pessoais.en_sexo} onChange={(e) => handleComplementarChange('dados_pessoais', 'en_sexo', e.target.value)}>
                        <MenuItem value="M">Masculino</MenuItem>
                        <MenuItem value="F">Feminino</MenuItem>
                        <MenuItem value="O">Outro</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Estado Civil</InputLabel>
                      <Select label="Estado Civil" value={formData.st_dados_complementares.dados_pessoais.en_estado_civil} onChange={(e) => handleComplementarChange('dados_pessoais', 'en_estado_civil', e.target.value)}>
                        <MenuItem value="solteiro">Solteiro(a)</MenuItem>
                        <MenuItem value="casado">Casado(a)</MenuItem>
                        <MenuItem value="divorciado">Divorciado(a)</MenuItem>
                        <MenuItem value="viuvo">Viúvo(a)</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField fullWidth label="Nacionalidade" value={formData.st_dados_complementares.dados_pessoais.st_nacionalidade} onChange={(e) => handleComplementarChange('dados_pessoais', 'st_nacionalidade', e.target.value)} />
                  </Box>
                </Box>
              </FormSection>

              <FormSection title="Origem & Filiação">
                {/* Linha 2 - Naturalidade */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Naturalidade (Cidade/Estado)" value={formData.st_dados_complementares.dados_pessoais.st_naturalidade} onChange={(e) => handleComplementarChange('dados_pessoais', 'st_naturalidade', e.target.value)} />
                  {/* Linha 3 - Pais */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Nome da Mãe" value={formData.st_dados_complementares.dados_pessoais.st_nome_mae} onChange={(e) => handleComplementarChange('dados_pessoais', 'st_nome_mae', e.target.value)} />
                    <TextField fullWidth label="Nome do Pai (Opcional)" value={formData.st_dados_complementares.dados_pessoais.st_nome_pai} onChange={(e) => handleComplementarChange('dados_pessoais', 'st_nome_pai', e.target.value)} />
                  </Box>
                </Box>
              </FormSection>

              <FormSection title="Lista de Dependentes" action={<Button variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ transform: 'rotate(90deg)' }} />} onClick={addDependente}>Novo Dependente</Button>}>
                <Stack spacing={1}>
                  {(formData.st_dados_complementares.dependentes || []).map((dep, index) => (
                    <Box key={index} sx={{ display: 'flex', gap: 2, bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                      <TextField fullWidth label="Nome Completo" value={dep.nome} onChange={(e) => handleDependenteChange(index, 'nome', e.target.value)} />
                      <TextField fullWidth label="Nascimento" value={dep.dt_nascimento} onChange={(e) => handleDependenteChange(index, 'dt_nascimento', e.target.value)} placeholder="DD/MM/AAAA" inputProps={{ maxLength: 10 }} />
                      <FormControl fullWidth sx={sharedStyles.formField}>
                        <InputLabel>Parentesco</InputLabel>
                        <Select label="Parentesco" value={dep.parentesco} onChange={(e) => handleDependenteChange(index, 'parentesco', e.target.value)}>
                          <MenuItem value="filho">Filho(a)</MenuItem>
                          <MenuItem value="conjuge">Cônjuge</MenuItem>
                          <MenuItem value="pais">Pai/Mãe</MenuItem>
                          <MenuItem value="outro">Outro</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField fullWidth label="CPF" value={dep.cpf} onChange={(e) => handleDependenteChange(index, 'cpf', e.target.value)} />
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton color="error" onClick={() => removeDependente(index)}><DeleteIcon /></IconButton>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "Documentos",
          content: (
            <TabContainer>
              <FormSection title="Documentos Principais">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Linha 1 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="CPF" value={formData.st_dados_complementares.documentos.cpf} onChange={(e) => handleComplementarChange('documentos', 'cpf', e.target.value)} placeholder="000.000.000-00" />
                    <TextField fullWidth label="PIS/PASEP" value={formData.st_dados_complementares.documentos.pis_pasep} onChange={(e) => handleComplementarChange('documentos', 'pis_pasep', e.target.value)} />
                    <TextField fullWidth label="CNH" value={formData.st_dados_complementares.documentos.cnh} onChange={(e) => handleComplementarChange('documentos', 'cnh', e.target.value)} />
                    <TextField fullWidth label="Reservista" value={formData.st_dados_complementares.documentos.certificado_reservista} onChange={(e) => handleComplementarChange('documentos', 'certificado_reservista', e.target.value)} />
                  </Box>

                  {/* Linha 2 - RG */}
                  <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">RG (IDENTIDADE)</Typography></Divider>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Número" value={formData.st_dados_complementares.documentos.rg.numero} onChange={(e) => handleComplementarChange('documentos', 'rg', e.target.value, 'numero')} />
                    <TextField fullWidth label="Órgão Emissor" value={formData.st_dados_complementares.documentos.rg.orgao} onChange={(e) => handleComplementarChange('documentos', 'rg', e.target.value, 'orgao')} />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>UF</InputLabel>
                      <Select label="UF" value={formData.st_dados_complementares.documentos.rg.uf} onChange={(e) => handleComplementarChange('documentos', 'rg', e.target.value, 'uf')}>
                        {ESTADOS_BRASILEIROS.map(est => <MenuItem key={est.uf} value={est.uf}>{est.uf}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Linha 3 - Título */}
                  <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">TÍTULO DE ELEITOR</Typography></Divider>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Número" value={formData.st_dados_complementares.documentos.titulo_eleitor.numero} onChange={(e) => handleComplementarChange('documentos', 'titulo_eleitor', e.target.value, 'numero')} />
                    <TextField fullWidth label="Zona" value={formData.st_dados_complementares.documentos.titulo_eleitor.zona} onChange={(e) => handleComplementarChange('documentos', 'titulo_eleitor', e.target.value, 'zona')} />
                    <TextField fullWidth label="Seção" value={formData.st_dados_complementares.documentos.titulo_eleitor.secao} onChange={(e) => handleComplementarChange('documentos', 'titulo_eleitor', e.target.value, 'secao')} />
                  </Box>

                  {/* Linha 4 - CTPS */}
                  <Divider sx={{ my: 2 }}><Typography variant="caption" color="text.secondary">CARTEIRA DE TRABALHO</Typography></Divider>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Número" value={formData.st_dados_complementares.documentos.ctps.numero} onChange={(e) => handleComplementarChange('documentos', 'ctps', e.target.value, 'numero')} />
                    <TextField fullWidth label="Série" value={formData.st_dados_complementares.documentos.ctps.serie} onChange={(e) => handleComplementarChange('documentos', 'ctps', e.target.value, 'serie')} />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>UF</InputLabel>
                      <Select label="UF" value={formData.st_dados_complementares.documentos.ctps.uf} onChange={(e) => handleComplementarChange('documentos', 'ctps', e.target.value, 'uf')}>
                        {ESTADOS_BRASILEIROS.map(est => <MenuItem key={est.uf} value={est.uf}>{est.uf}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "Endereço",
          content: (
            <TabContainer>
              <FormSection title="Endereço Principal">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Linha 1 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="CEP (Busca Automática)" value={formData.st_dados_complementares.endereco.cep} onChange={(e) => handleComplementarChange('endereco', 'cep', e.target.value)} placeholder="00000-000" InputProps={{ endAdornment: loading ? <InputAdornment position="end">...</InputAdornment> : null }} />
                    <TextField fullWidth label="Logradouro" value={formData.st_dados_complementares.endereco.logradouro} onChange={(e) => handleComplementarChange('endereco', 'logradouro', e.target.value)} />
                  </Box>
                  {/* Linha 2 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Número" value={formData.st_dados_complementares.endereco.numero} onChange={(e) => handleComplementarChange('endereco', 'numero', e.target.value)} />
                    <TextField fullWidth label="Bairro" value={formData.st_dados_complementares.endereco.bairro} onChange={(e) => handleComplementarChange('endereco', 'bairro', e.target.value)} />
                    <TextField fullWidth label="Cidade" value={formData.st_dados_complementares.endereco.cidade} onChange={(e) => handleComplementarChange('endereco', 'cidade', e.target.value)} />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Estado (UF)</InputLabel>
                      <Select label="Estado (UF)" value={formData.st_dados_complementares.endereco.estado} onChange={(e) => handleComplementarChange('endereco', 'estado', e.target.value)}>
                        {ESTADOS_BRASILEIROS.map(est => <MenuItem key={est.uf} value={est.uf}>{est.nome}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "Financeiro/Contrato",
          content: (
            <TabContainer>
              <FormSection title="Informações Bancárias">
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField fullWidth label="Banco" value={formData.st_dados_complementares.financeiro.banco} onChange={(e) => handleComplementarChange('financeiro', 'banco', e.target.value)} />
                  <TextField fullWidth label="Agência" value={formData.st_dados_complementares.financeiro.agencia} onChange={(e) => handleComplementarChange('financeiro', 'agencia', e.target.value)} />
                  <TextField fullWidth label="Conta" value={formData.st_dados_complementares.financeiro.conta} onChange={(e) => handleComplementarChange('financeiro', 'conta', e.target.value)} />
                  <FormControl fullWidth sx={sharedStyles.formField}>
                    <InputLabel>Tipo</InputLabel>
                    <Select label="Tipo" value={formData.st_dados_complementares.financeiro.tipo_conta} onChange={(e) => handleComplementarChange('financeiro', 'tipo_conta', e.target.value)}>
                      <MenuItem value="corrente">Corrente</MenuItem>
                      <MenuItem value="poupanca">Poupança</MenuItem>
                      <MenuItem value="salario">Salário</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField fullWidth label="Chave PIX" value={formData.st_dados_complementares.financeiro.chave_pix} onChange={(e) => handleComplementarChange('financeiro', 'chave_pix', e.target.value)} />
                </Box>
              </FormSection>

              <FormSection title="Dados do Contrato">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Linha 1 - Cargo */}
                  <TextField fullWidth label="Cargo/Função" value={formData.st_dados_complementares.contrato.cargo} onChange={(e) => handleComplementarChange('contrato', 'cargo', e.target.value)} />

                  {/* Linha 2 */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Setor/Departamento" value={formData.st_dados_complementares.contrato.setor} onChange={(e) => handleComplementarChange('contrato', 'setor', e.target.value)} />
                    <FormControl fullWidth sx={sharedStyles.formField}>
                      <InputLabel>Tipo de Contrato</InputLabel>
                      <Select label="Tipo de Contrato" value={formData.st_dados_complementares.contrato.tipo_contrato} onChange={(e) => handleComplementarChange('contrato', 'tipo_contrato', e.target.value)}>
                        <MenuItem value="clt">CLT</MenuItem>
                        <MenuItem value="pj">PJ</MenuItem>
                        <MenuItem value="temporario">Temporário</MenuItem>
                        <MenuItem value="estagio">Estágio</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField fullWidth label="Data de Admissão" value={formData.st_dados_complementares.contrato.dt_admissao} onChange={(e) => handleComplementarChange('contrato', 'dt_admissao', e.target.value)} placeholder="DD/MM/AAAA" inputProps={{ maxLength: 10 }} />
                    <TextField fullWidth label="Jornada de Trabalho" value={formData.st_dados_complementares.contrato.jornada} onChange={(e) => handleComplementarChange('contrato', 'jornada', e.target.value)} />
                  </Box>

                  {/* Linha 3 - Salário */}
                  <TextField fullWidth label="Salário" value={formData.st_dados_complementares.contrato.salario} onChange={(e) => handleComplementarChange('contrato', 'salario', e.target.value)} />

                  {/* Linha 4 - Benefícios */}
                  <TextField fullWidth multiline rows={2} label="Benefícios (Separar por vírgula)" placeholder="Ex: Vale Transporte, Vale Refeição, Plano de Saúde..." value={formData.st_dados_complementares.contrato.beneficios} onChange={(e) => handleComplementarChange('contrato', 'beneficios', e.target.value)} />
                </Box>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "RH/Documentos",
          content: (
            <TabContainer>
              <FormSection title="Currículo & Experiência">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Formação Acadêmica" value={formData.st_dados_complementares.rh.formacao} onChange={(e) => handleComplementarChange('rh', 'formacao', e.target.value)} />
                  <TextField fullWidth multiline rows={4} label="Cursos / Certificações" value={formData.st_dados_complementares.rh.cursos} onChange={(e) => handleComplementarChange('rh', 'cursos', e.target.value)} />
                  <TextField fullWidth multiline rows={4} label="Experiência Profissional" value={formData.st_dados_complementares.rh.experiencia} onChange={(e) => handleComplementarChange('rh', 'experiencia', e.target.value)} />
                </Box>
              </FormSection>

              <FormSection title="Contato de Emergência" variant="error">
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField fullWidth label="Nome do Responsável" value={formData.st_dados_complementares.rh.contato_emergencia.nome} onChange={(e) => handleComplementarChange('rh', 'contato_emergencia', e.target.value, 'nome')} />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField fullWidth label="Telefone" value={formData.st_dados_complementares.rh.contato_emergencia.telefone} onChange={(e) => handleComplementarChange('rh', 'contato_emergencia', e.target.value, 'telefone')} />
                    <TextField fullWidth label="Relação (Pai, Mãe, Cônjuge...)" value={formData.st_dados_complementares.rh.contato_emergencia.relacao} onChange={(e) => handleComplementarChange('rh', 'contato_emergencia', e.target.value, 'relacao')} />
                  </Box>
                </Box>
              </FormSection>

              <FormSection title="Arquivos Anexados">
                <Box sx={{ display: 'flex', gap: 2 }}>
                  {['foto_perfil', 'docs_pessoais', 'comprovante_residencia', 'aso'].map((docKey) => (
                    <Box key={docKey} sx={{ flex: 1, border: '1px dashed', borderColor: 'divider', p: 2, borderRadius: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: 'background.paper', transition: '0.2s', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}>
                      <Typography variant="caption" sx={{ mb: 1, fontWeight: 'bold', textTransform: 'uppercase', color: 'text.secondary' }}>
                        {docKey === 'foto_perfil' && 'Foto de Perfil'}
                        {docKey === 'docs_pessoais' && 'Documentos Pessoais'}
                        {docKey === 'comprovante_residencia' && 'Comp. Residência'}
                        {docKey === 'aso' && 'Exame ASO'}
                      </Typography>
                      {formData.st_dados_complementares.arquivos[docKey] ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" noWrap sx={{ maxWidth: 140, fontWeight: 'medium' }}>
                            {formData.st_dados_complementares.arquivos[docKey].name}
                          </Typography>
                          <IconButton size="small" color="error" onClick={() => removeFile(docKey)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button variant="text" component="label" startIcon={<CloudUploadIcon />} size="small" sx={{ textTransform: 'none' }}>
                          Selecionar
                          <input type="file" hidden accept="image/*,.pdf" onChange={(e) => handleFileChange(e, docKey)} />
                        </Button>
                      )}
                    </Box>
                  ))}
                </Box>
              </FormSection>
            </TabContainer>
          )
        },
        {
          label: "Termos & LGPD",
          content: (
            <TabContainer>
              <FormSection title="CONSENTIMENTO DE DADOS (LGPD)">
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Checkbox
                    checked={formData.st_dados_complementares.lgpd_consentimento}
                    onChange={(e) => setFormData(prev => ({ ...prev, st_dados_complementares: { ...prev.st_dados_complementares, lgpd_consentimento: e.target.checked } }))}
                  />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Declaro que li e aceito os termos de uso e política de privacidade.
                  </Typography>
                </Box>
                <Button startIcon={<PrintIcon />} variant="outlined" onClick={handlePrintLGPD}>
                  Termo de Consentimento
                </Button>
              </FormSection>

              <FormSection title="Observações Gerais">
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Observações Adicionais"
                  value={formData.st_dados_complementares.st_observacoes}
                  onChange={(e) => setFormData(prev => ({ ...prev, st_dados_complementares: { ...prev.st_dados_complementares, st_observacoes: e.target.value } }))}
                />
              </FormSection>
            </TabContainer>
          )
        }
      ]}
    />
  );
}
